import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useSession, whereWeWere } from './auth/session.tsx';
import { SignIn } from './pages/SignIn.tsx';
import { ClientDashboard } from './pages/ClientDashboard.tsx';
import { RecordVisit } from './pages/RecordVisit.tsx';
import { ClientList } from './pages/ClientList.tsx';
import { Today } from './pages/Today.tsx';
import { DirectorDashboard } from './pages/DirectorDashboard.tsx';
import { ComplianceDashboard } from './pages/ComplianceDashboard.tsx';
import { CommissionTracker } from './pages/CommissionTracker.tsx';
import { FinanceDashboard } from './pages/FinanceDashboard.tsx';
import { ExecutiveDashboard } from './pages/ExecutiveDashboard.tsx';
import { DashboardsIndex } from './components/DashboardFrame.tsx';
import { AppShell } from './components/AppShell.tsx';
import { CompanyPolicies } from './pages/CompanyPolicies.tsx';
import { GeocodeBacklog } from './pages/GeocodeBacklog.tsx';
import { AddClient } from './pages/AddClient.tsx';
import { RealEstate } from './pages/RealEstate.tsx';
import { UntypedBuildings } from './pages/UntypedBuildings.tsx';
import { BreakGlassUse } from './pages/BreakGlassUse.tsx';
import { useMe } from './api/queries.ts';

/** Where each role lands. Explicit, because inferring from tier would quietly
 *  mis-land a role added later. Only /clients and the client dashboard exist
 *  today, so most roles land on the same place for now. */
const LANDING: Record<string, string> = {
  // Every role lands somewhere that exists.
  //
  // These pointed at /work and /visits, which are both `NotBuiltYet`. A case
  // manager — the role this application is mostly for — signed in and was shown
  // "Today is not built yet", which is a worse first impression than the
  // client list and tells them nothing about whether anything works.
  //
  // The intended destinations are kept beside each entry rather than in a
  // commit message, so restoring one is a one-word edit when the screen exists.
  // Tasks due (/work) is built and linked from the client list, but it is not
  // where anyone lands yet. A case manager with no tasks assigned — arthur.fain
  // on Azure, whose clients' tasks are unassigned — would open the application
  // on "No open tasks are assigned to you". The client list, narrowed to their
  // caseload, is the honest first screen. Point these at /work once tasks are
  // actually held by the people they belong to.
  CASE_MGR_SW: '/clients',     // → /work once tasks are assigned
  CASE_MGR_FIN: '/clients',    // → /work
  PARALEGAL: '/clients',       // → /work
  DIR_SOCIAL_WORK: '/clients', // → /visits once Visits due is built
  CASE_REVIEWER: '/clients',   // → /unassigned once v_needs_review is exposed
  DIR_COMPLIANCE: '/clients',  // → her queue is v_report_readiness, phase 2
};

function Landing() {
  const me = useMe();
  // isPending, not isLoading — the same distinction as in ClientDashboard.
  // Between retries isLoading drops to false while the query still has no
  // data, which would land the user on the fallback rather than their own
  // landing page, on exactly the connection where that is hardest to notice.
  if (me.isPending) return <p className="p-6 text-neutral-500">Loading…</p>;
  const to = (me.data && LANDING[me.data.role.code]) ?? '/clients';
  return <Navigate to={to} replace />;
}

/** Placeholder until the lists are built. */
function NotBuiltYet({ what }: { what: string }) {
  const me = useMe();
  return (
    <div className="p-6">
      <p className="text-neutral-500">
        {what} is not built yet.
        {me.data && ` Signed in as ${me.data.fullName} (${me.data.role.name}).`}
      </p>
    </div>
  );
}

export function App() {
  const { isSignedIn } = useSession();
  const navigate = useNavigate();

  // After a sign-in redirect, put the user back where they were.
  useEffect(() => {
    if (!isSignedIn) return;
    const to = whereWeWere();
    if (to && to !== location.pathname + location.search) navigate(to, { replace: true });
  }, [isSignedIn, navigate]);

  if (!isSignedIn) return <SignIn />;

  return (
    <AppShell>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/policies" element={<CompanyPolicies />} />
      <Route path="/geocoding" element={<GeocodeBacklog />} />
      <Route path="/real-estate" element={<RealEstate />} />
      <Route path="/facilities/needing-type" element={<UntypedBuildings />} />
      <Route path="/break-glass" element={<BreakGlassUse />} />
      {/*
        Before /clients/:id for readability, though it does not depend on the
        order: React Router ranks a static segment above a dynamic one, so
        "new" cannot be swallowed by ":id" however these are declared. Written
        first anyway, because a reader should not have to know that to be sure.
      */}
      <Route path="/clients/new" element={<AddClient />} />
      <Route path="/clients/:id" element={<ClientDashboard />} />
      <Route path="/clients/:id/visit" element={<RecordVisit />} />
      <Route path="/clients" element={<ClientList />} />
      <Route path="/work" element={<Today />} />
      <Route path="/dashboards" element={<DashboardsIndex />} />
      <Route path="/dashboards/executive" element={<ExecutiveDashboard />} />
      <Route path="/dashboards/director" element={<DirectorDashboard />} />
      <Route path="/dashboards/compliance" element={<ComplianceDashboard />} />
      <Route path="/dashboards/commission" element={<CommissionTracker />} />
      <Route path="/dashboards/finance" element={<FinanceDashboard />} />
      <Route path="/visits" element={<NotBuiltYet what="Visits due" />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </AppShell>
  );
}
